/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-15 15:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_THEMES_REQUEST,
  GET_THEMES_SUCCESS,
  GET_THEMES_FAILURE,
  GET_ALL_THEMES_V3_REQUEST,
  GET_ALL_THEMES_V3_SUCCESS,
  GET_ALL_THEMES_V3_FAILURE,
  CHECK_THEME_V3_REQUEST,
  CHECK_THEME_V3_SUCCESS,
  CHECK_THEME_V3_FAILURE,
  SETUP_THEME_V3_REQUEST,
  SETUP_THEME_V3_SUCCESS,
  SETUP_THEME_V3_FAILURE,
  REVERT_THEME_V3_REQUEST,
  REVERT_THEME_V3_SUCCESS,
  REVERT_THEME_V3_FAILURE,
  REMOVE_OLD_THEME_REQUEST,
  REMOVE_OLD_THEME_SUCCESS,
  REMOVE_OLD_THEME_FAILURE,
  UPGRADE_THEME_V3_REQUEST,
  UPGRADE_THEME_V3_SUCCESS,
  UPGRADE_THEME_V3_FAILURE,
  RESET_THEME_SETUP_V3,
  GET_MAIN_THEME_SETUP_STATUS_FAILURE,
  GET_MAIN_THEME_SETUP_STATUS_SUCCESS,
  GET_MAIN_THEME_SETUP_STATUS_REQUEST
} from 'stores/flow-type/theme-setup-v3.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get list of installed themes api
 * @return {object} The RSAA object for API middleware
 */
export const getThemes = (i18n: I18n, controlledFetch = false) => {
  return apiService.sendRequest('admin-v3.themes.get-themes', {}, [
    {
      type: GET_THEMES_REQUEST,
      meta: controlledFetch
        ? {
            controlledFetch,
            dataStatePath: 'themeSetup.themes',
            loadingStatePath: 'themeSetup.getThemesLoading'
          }
        : undefined
    },
    {
      type: GET_THEMES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_THEMES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.getThemes'),
          i18n
        )
    }
  ]);
};

/**
 * call to get list of all themes api (v3 api, v3 of update phase, not theme v3)
 * @return {object} The RSAA object for API middleware
 */
export const getAllThemes = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup-v3.list-themes', {}, [
    GET_ALL_THEMES_V3_REQUEST,
    {
      type: GET_ALL_THEMES_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ALL_THEMES_V3_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
          i18n
        )
    }
  ]);
};

/**
 * call to check theme for install api (v3 api, v3 of update phase, not theme v3)
 * @return {object} The RSAA object for API middleware
 */
export const checkTheme = (themeId: string, i18n: I18n) => {
  const body = { themeId };
  return apiService.sendRequest('admin-v3.theme-setup-v3.tae-setup-compatible', { body }, [
    CHECK_THEME_V3_REQUEST,
    {
      type: CHECK_THEME_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data, themeId };
      }
    },
    {
      type: CHECK_THEME_V3_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.checkTheme'),
          i18n
        )
    }
  ]);
};

/**
 * call to check theme for install api (v3 api, v3 of update phase, not theme v3)
 * @return {object} The RSAA object for API middleware
 */
export const checkThemeWithTurbo = (themeId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup-v3.get-theme-info', { params: { themeId } }, [
    CHECK_THEME_V3_REQUEST,
    {
      type: CHECK_THEME_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data, themeId };
      }
    },
    {
      type: CHECK_THEME_V3_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.checkTheme'),
          i18n
        )
    }
  ]);
};

/**
 * call to install theme api
 * @return {object} The RSAA object for API middleware
 */
export const setupThemeV3 = (body, meta: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup-v3.tae-setup-install', { body }, [
    SETUP_THEME_V3_REQUEST,
    {
      type: SETUP_THEME_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      },
      meta
    },
    {
      type: SETUP_THEME_V3_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.setupTheme'),
          i18n
        );

        return errorResult;
      },
      meta
    }
  ]);
};

/**
 * call to revert theme api
 * @return {object} The RSAA object for API middleware
 */
export const revertTheme = (body: Object, i18n: I18n) => {
  const { themeId, theme, removeFeatures } = body;

  return apiService.sendRequest('admin-v3.theme-setup-v3.tae-setup-remove', { body }, [
    REVERT_THEME_V3_REQUEST,
    {
      type: REVERT_THEME_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertTheme_Success'),
          i18n
        );

        mixpanelV3Helper.trackEventData('Remove app features', {
          feature: 'Theme Setup',
          selectedTheme: themeId,
          selectedThemeName: theme?.name,
          recommendationWidget: body?.removeFeatures?.includes('recommendation'),
          collectionPageFilters: body?.removeFeatures?.includes('filterCollection'),
          searchPageFilters: body?.removeFeatures?.includes('filterSearch'),
          instantSearchWidget: body?.removeFeatures?.includes('instantSearch')
        });

        // Track Full Story event
        trackFSEvent('Remove app features', {
          feature: 'Theme Setup',
          selectedTheme: themeId,
          selectedThemeName: theme?.name,
          recommendationWidget: body?.removeFeatures?.includes('recommendation'),
          collectionPageFilters: body?.removeFeatures?.includes('filterCollection'),
          searchPageFilters: body?.removeFeatures?.includes('filterSearch'),
          instantSearchWidget: body?.removeFeatures?.includes('instantSearch')
        });

        return { data, themeId, theme, removeFeatures };
      }
    },
    {
      type: REVERT_THEME_V3_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertTheme_Failure'),
          i18n
        );

        return _.assign(errorResult, { themeId });
      }
    }
  ]);
};

/**
 * call to revert theme api
 * @return {object} The RSAA object for API middleware
 */
export const removeOldVersionTheme = (body: Object, i18n: I18n) => {
  const { themeId, theme } = body;

  return apiService.sendRequest('admin-v3.theme-setup-v3.remove-all-theme-libs', { body }, [
    REMOVE_OLD_THEME_REQUEST,
    {
      type: REMOVE_OLD_THEME_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertOldTheme_Success'),
          i18n
        );

        mixpanelV3Helper.trackEventData('Remove former version', {
          feature: 'Theme Setup',
          selectedTheme: themeId,
          selectedThemeName: theme?.name
        });

        // Track Full Story event
        trackFSEvent('Remove old version', {
          feature: 'Theme Setup',
          selectedTheme: themeId,
          selectedThemeName: theme?.name
        });
        return { data, themeId, theme };
      }
    },
    {
      type: REMOVE_OLD_THEME_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertTheme_Failure'),
          i18n
        );
        return _.assign(errorResult, { themeId });
      }
    }
  ]);
};

/**
 * call to upgrade theme api
 * @return {object} The RSAA object for API middleware
 */
export const upgradeTheme = (body: Object, i18n: I18n) => {
  const { themeId } = body;
  const theme = body;

  return apiService.sendRequest('admin-v3.theme-setup-v3.upgrade', { body }, [
    UPGRADE_THEME_V3_REQUEST,
    {
      type: UPGRADE_THEME_V3_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.ThemeSetup.Actions.upgradeTheme_Success'),
          i18n
        );

        return { data, themeId, theme };
      }
    },
    {
      type: UPGRADE_THEME_V3_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.upgradeTheme_Failure'),
          i18n
        );

        return _.assign(errorResult, { themeId });
      }
    }
  ]);
};

/**
 * call to get main theme setup status
 * @return {object} The RSAA object for API middleware
 */
export const getMainThemeSetupStatus = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup-v3.main-theme-setup-status', {}, [
    GET_MAIN_THEME_SETUP_STATUS_REQUEST,
    {
      type: GET_MAIN_THEME_SETUP_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      }
    },
    {
      type: GET_MAIN_THEME_SETUP_STATUS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.getThemes'),
          i18n
        )
    }
  ]);
};

/**
 * call to reset state
 * @return {object} The RSAA object for API middleware
 */
export const unmountComponent = () => {
  return { type: RESET_THEME_SETUP_V3 };
};
